import React from 'react';
import { Helmet } from 'react-helmet-async';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy - How We Protect Your Data | Trademark Essential</title>
        <meta name="description" content="Our privacy policy explains how Trademark Essential collects, uses, and protects your personal information. Your data security is our priority." />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h1 className="text-4xl md:text-5xl font-bold text-[#2B0A12] mb-8">Privacy Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed mb-6">
            At <strong>Trademark Essential</strong> we are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This Privacy Policy outlines how we collect, use, and protect your personal data, and it applies to all interactions with our website, products, and services.
          </p>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Information We Collect</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We collect personal information from you when you visit our website, register for an account, make a purchase, or communicate with us. The types of information we may collect include:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li><strong>Personal Identification Information:</strong> Name, email address, phone number, billing and shipping addresses, and payment information.</li>
            <li><strong>Non-personal Identification Information:</strong> IP address, browser type, operating system, and website usage information.</li>
            <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic, and personalize content.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">How We Use Your Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We use the information we collect for various purposes, including:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li>To process and manage your transactions and orders.</li>
            <li>To communicate with you about your orders, services, or updates.</li>
            <li>To respond to your inquiries and provide customer support.</li>
            <li>To improve our website, products, and services.</li>
            <li>To send promotional materials, newsletters, and other marketing communications (if you have opted-in to receive them).</li>
            <li>To prevent fraud and ensure the security of our website.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">How We Protect Your Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We implement a variety of security measures to maintain the safety of your personal information, including:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li><strong>Encryption:</strong> We use SSL encryption technology to protect sensitive data during transmission.</li>
            <li><strong>Secure Storage:</strong> Your personal information is stored in a secure environment with restricted access.</li>
            <li><strong>Regular Audits:</strong> We regularly review our security practices to ensure that your data is safeguarded.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Sharing Your Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following cases:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li><strong>Service Providers:</strong> We may share your data with trusted third-party service providers who assist us in operating our website, processing payments, or providing customer service. These providers are obligated to maintain the confidentiality and security of your information.</li>
            <li><strong>Legal Compliance:</strong> We may disclose your information to comply with legal obligations, enforce our site policies, or protect our rights or the rights of others.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Your Rights and Choices</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            You have the following rights regarding your personal information:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700 space-y-2">
            <li><strong>Access and Correction:</strong> You may request access to your personal information and request corrections or updates to your data.</li>
            <li><strong>Opt-Out:</strong> If you no longer wish to receive marketing communications from us, you can opt-out by clicking the unsubscribe link in any email or by contacting us directly.</li>
            <li><strong>Deletion:</strong> You may request that we delete your personal information, subject to applicable legal and regulatory obligations.</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Cookies and Tracking Technologies</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We use cookies and similar technologies to enhance your experience on our website. You can choose to disable cookies through your browser settings, but this may affect your ability to access certain features of our website. For more information, please refer to our Cookie Policy.
          </p>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Data Retention</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including legal, accounting, or reporting requirements.
          </p>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Third-Party Links</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites, and we encourage you to review their privacy policies before providing any personal information.
          </p>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Children's Privacy</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Our website is not intended for children under the age of 13. We do not knowingly collect or solicit personal information from children. If we learn that we have inadvertently collected such information, we will delete it as soon as possible.
          </p>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Changes to This Privacy Policy</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We reserve the right to modify or update this Privacy Policy at any time. Any changes will be posted on this page with an updated "Effective Date." We encourage you to review this policy periodically to stay informed about how we are protecting your information.
          </p>

          <h2 className="text-2xl font-bold text-[#2B0A12] mt-8 mb-4">Contact Us</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            If you have any questions or concerns about this Privacy Policy or how we handle your personal information, please contact us at:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <p className="text-gray-700"><strong>Company:</strong> Trademark Essential</p>
            <p className="text-gray-700"><strong>Address:</strong> 7060 Hollywood Blvd, Los Angeles, CA 90028</p>
            <p className="text-gray-700"><strong>Phone:</strong> +1 (888) 203-1297</p>
          </div>

          <p className="text-gray-700 leading-relaxed">
            By using our website and services, you consent to the terms of this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
